import lottie, { AnimationItem } from 'lottie-web';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAnalytics } from '@/hooks/analytics';
import { useTimeout } from '@/hooks/utils';

import messages from './messages';
import {
	BreatheCount,
	Bubble,
	BubbleCopy,
	Container,
	OuterCircle,
	OuterRing,
	ProgressMarker,
} from './styles';
import { BreathAction, BreathType } from './types';

type BreatheBubbleProps = {
	bubblePath: string;
	ringPath: string;
	breathAction: BreathAction[];
	shouldRenderRemainingBreathCopy: boolean;
	maxBreaths?: number;
	isMuted?: boolean;
};

export default function BreatheBubble({
	bubblePath,
	ringPath,
	breathAction,
	maxBreaths,
	shouldRenderRemainingBreathCopy,
	isMuted = false,
}: BreatheBubbleProps): JSX.Element {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();

	const [remainingBreathCopy, setRemainingBreathCopy] = useState('');
	const [breatheCopy, setBreatheCopy] = useState(breathAction[0].instruction);
	const [actionNumber, setActionNumber] = useState(0);
	const [totalBreathsTaken, setTotalBreathsTaken] = useState(0);
	const [animate, setAnimate] = useState<AnimationItem | undefined>(undefined);
	const bubbleRef = useRef<HTMLDivElement>(null);
	const ringRef = useRef<HTMLDivElement>(null);
	const progressRef = useRef<HTMLDivElement>(null);

	const currentAction = breathAction[actionNumber];

	const timeoutCallback = useCallback(() => {
		if (actionNumber === breathAction.length - 1) {
			setTotalBreathsTaken(_totalBreathsTaken => _totalBreathsTaken + 1);
			setActionNumber(0);
		} else {
			setActionNumber(actionNumber + 1);
		}
	}, [actionNumber, breathAction.length]);

	useTimeout(timeoutCallback, currentAction.duration * 1000);

	useEffect(
		() => {
			logEvent({
				eventName: 'Breathe Bubble : Entered',
			});

			return () => {
				logEvent({
					eventName: 'Breathe Bubble : Exit',
				});
			};
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(
		() => {
			if (maxBreaths) {
				if (totalBreathsTaken === maxBreaths - 2) {
					setRemainingBreathCopy(formatMessage(messages.twoMoreBreaths));
				} else if (totalBreathsTaken === maxBreaths - 1) {
					setRemainingBreathCopy(formatMessage(messages.lastBreath));
				}
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[totalBreathsTaken],
	);

	useEffect(
		() => {
			currentAction.audio.stop();
			setBreatheCopy(breathAction[actionNumber].instruction);
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actionNumber],
	);

	useEffect(
		() => {
			if (animate) {
				if (currentAction.type === BreathType.HOLD) {
					animate.pause();
				} else {
					if (animate.isPaused) {
						animate.play();
					}
					animate.setSpeed(1.0 / currentAction.duration);
				}
			}
			currentAction.audio.play();
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentAction],
	);

	useEffect(() => {
		if (isMuted) {
			breathAction.forEach(action => action.audio.mute(true));
		} else {
			breathAction.forEach(action => action.audio.mute(false));
		}
	}, [breathAction, isMuted]);

	useEffect(
		() => {
			if (bubbleRef.current) {
				const bubble = lottie.loadAnimation({
					container: bubbleRef.current,
					renderer: 'svg',
					loop: true,
					autoplay: true,
					path: bubblePath,
				});
				setAnimate(bubble);
				bubble.setSpeed(1.0 / currentAction.duration);
			}
			if (ringRef.current) {
				lottie.loadAnimation({
					container: ringRef.current,
					renderer: 'svg',
					loop: true,
					autoplay: true,
					path: ringPath,
				});
			}
			if (progressRef.current) {
				const rotatingNotch = lottie.loadAnimation({
					container: progressRef.current,
					renderer: 'svg',
					loop: true,
					autoplay: true,
					path: '/_n/lottie/breathe-progress-marker.json',
				});
				rotatingNotch.setSpeed(1.0 / (currentAction.duration + 1));
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[bubblePath, ringPath],
	);

	return (
		<Container>
			<OuterCircle>
				<OuterRing ref={ringRef} />
				<Bubble ref={bubbleRef} />
				<BubbleCopy>{breatheCopy}</BubbleCopy>
				<ProgressMarker ref={progressRef} />
			</OuterCircle>
			{shouldRenderRemainingBreathCopy ? <BreatheCount>{remainingBreathCopy}</BreatheCount> : null}
		</Container>
	);
}
